// ────────────────────────────────────────────────────────────────────────
// CustomFabricQuestionnaire — opens when the tailor clicks an assigned
// fabric in the Custom Fabric tab. Replaces the "click fabric → click
// silhouette → auto-generate" flow which was too eager and gave the
// tailor no say over shirt/shoe pairing.
//
// Mirrors the WeddingPartyQuestionnaire visual language. Sections:
//   1. Category + Silhouette (what to render)
//   2. Shoe — style + colour
//   3. Shirt — style + colour (side-by-side with shoe on desktop)
//
// On submit, returns a full combo payload to Dashboard which fires the
// existing customFabricFromButton path with the specifics applied.
// ────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Loader2, Sparkles, Footprints, Shirt as ShirtIcon, X } from 'lucide-react';
import { toast } from 'sonner';
// Iter 109 v5 — reuse the trouser palette from CustomizeModal so the
// pant colour dictated pre-render matches what a tailor would pick in
// the post-render pant editor. Single source of truth.
import { PANT_COLORS } from './CustomizeModal';
import TrouserRestructureBlock, {
  buildTrouserStyleTokens,
} from './TrouserRestructureBlock';

// Same silhouette tree the Custom Fabric tab uses, so the questionnaire
// presents identical coverage regardless of which entry-point opened it.
const SILHOUETTE_TREE = [
  {
    key: 'full_suits', label: 'Suits',
    silhouettes: [
      { id: 'classic_suit',    label: 'Single-Breasted Suit',  garment: 'Single-Breasted Suit' },
      { id: 'double_breasted', label: 'Double-Breasted Suit',  garment: 'Double-Breasted Suit' },
      { id: 'three_piece',     label: 'Three-Piece Suit',      garment: 'Three-Piece Suit' },
      { id: 'tuxedo',          label: 'Tuxedo',                garment: 'Tuxedo' },
    ],
  },
  {
    key: 'business_casual', label: 'Sports Coat',
    silhouettes: [
      { id: 'single_blazer', label: 'Single-Breasted Sports Coat',  garment: 'Sports Coat' },
      { id: 'double_blazer', label: 'Double-Breasted Sports Coat',  garment: 'Double-Breasted Sports Coat' },
    ],
  },
  {
    key: 'outerwear', label: 'Overcoats',
    silhouettes: [
      { id: 'overcoat',    label: 'Overcoat',     garment: 'Overcoat' },
      { id: 'car_coat',    label: 'Car Coat',     garment: 'Car Coat' },
      { id: 'trench_coat', label: 'Trench Coat',  garment: 'Trench Coat' },
    ],
  },
  {
    key: 'shirts', label: 'Shirts',
    silhouettes: [
      { id: 'classic_dress_shirt', label: 'Classic Dress Shirt',  garment: 'Classic Dress Shirt' },
      { id: 'spread_collar',       label: 'Spread Collar Shirt',  garment: 'Spread Collar Shirt' },
      { id: 'oxford_shirt',        label: 'Oxford Button-Down',   garment: 'Oxford Button-Down Shirt' },
      { id: 'polo',                label: 'Polo Shirt',           garment: 'Polo Shirt' },
    ],
  },
];

const SHOE_STYLES = [
  { id: 'oxford',         label: 'Oxford' },
  { id: 'derby',          label: 'Derby' },
  { id: 'loafer',         label: 'Loafer' },
  // Iter 156 — id aligned with backend structural_only_lock which
  // expects `monk_strap` (not `monk`). Was previously `monk` and
  // the CFQ pick fell through the shoe-directive map unmatched.
  { id: 'monk_strap',     label: 'Monk-Strap' },
  { id: 'chelsea_boot',   label: 'Chelsea Boot' },
  { id: 'cap_toe',        label: 'Cap-Toe Oxford' },
  // Feb 2026 — casual white court sneaker.
  { id: 'sneaker',        label: 'Sneaker' },
];

const SHOE_COLOURS = [
  { id: 'Black',          label: 'Black',         hex: '#0A0A0C' },
  { id: 'Dark Brown',     label: 'Dark Brown',    hex: '#3A2418' },
  { id: 'Chocolate',      label: 'Chocolate',     hex: '#503020' },
  { id: 'Cognac',         label: 'Cognac',        hex: '#9B5E2E' },
  { id: 'Tan',            label: 'Tan',           hex: '#C19A6B' },
  { id: 'Oxblood',        label: 'Oxblood',       hex: '#582430' },
  { id: 'White',          label: 'White',         hex: '#F5F0E1' },
];

const SHIRT_STYLES = [
  { id: 'spread_collar',  label: 'Spread Collar' },
  { id: 'point_collar',   label: 'Point Collar' },
  { id: 'cutaway_collar', label: 'Cutaway Collar' },
  { id: 'button_down',    label: 'Button-Down' },
  { id: 'club_collar',    label: 'Club Collar' },
  { id: 'band_collar',    label: 'Band / No Collar' },
];

const SHIRT_COLOURS = [
  { id: 'White',          label: 'White',           hex: '#FFFFFF' },
  { id: 'Light Blue',     label: 'Light Blue',      hex: '#BFD9F2' },
  { id: 'Cream',          label: 'Cream',           hex: '#F5F0E1' },
  { id: 'Light Pink',     label: 'Light Pink',      hex: '#F5D7D9' },
  { id: 'Pale Lavender',  label: 'Pale Lavender',   hex: '#DCD0EC' },
  { id: 'Light Grey',     label: 'Light Grey',      hex: '#D9D9D9' },
  { id: 'Pale Mint',      label: 'Pale Mint',       hex: '#CDE3D2' },
  { id: 'Black',          label: 'Black',           hex: '#1A1A1A' },
];

// Iter 127 — jacket construction selectors (hidden for shirts).
const LAPEL_STYLES = [
  { id: 'notch', label: 'Notch',  hint: 'The everyday standard' },
  { id: 'peak',  label: 'Peak',   hint: 'Formal · powerful' },
  { id: 'shawl', label: 'Shawl',  hint: 'Tuxedo · rounded' },
];

const POCKET_CHOICES = [
  { id: '',        label: 'No Preference' },
  { id: 'flapped', label: 'Flapped' },
  { id: 'jetted',  label: 'Jetted' },
  { id: 'patched', label: 'Patched' },
];

// Feb 2026 — Vest / waistcoat customisation. Surfaced ONLY for
// three-piece suits and tuxedos. SB and DB expose different button
// configurations because the physical construction differs (SB = one
// column of 4/5/6 buttons, DB = two parallel columns as 4×2 or 6×2).
const VEST_STYLES = [
  { id: 'single_breasted', label: 'Single-Breasted', hint: 'V-neck · one column' },
  { id: 'double_breasted', label: 'Double-Breasted', hint: 'Ceremonial · two columns' },
];
const VEST_BUTTONS_SB = [
  { id: '4', label: '4 buttons' },
  { id: '5', label: '5 buttons' },
  { id: '6', label: '6 buttons' },
];
const VEST_BUTTONS_DB = [
  { id: '4x2', label: '4×2' },
  { id: '6x2', label: '6×2' },
];
const VEST_LAPELS_SB = [
  { id: 'none',  label: 'No lapel', hint: 'Clean V-neck' },
  { id: 'notch', label: 'Notch',    hint: 'Subtle revere' },
  { id: 'shawl', label: 'Shawl',    hint: 'Tuxedo · rounded' },
];
const VEST_LAPELS_DB = [
  { id: 'none',  label: 'No lapel', hint: 'Clean button line' },
  { id: 'peak',  label: 'Peak',    hint: 'Formal · pointed' },
  { id: 'shawl', label: 'Shawl',   hint: 'Tuxedo · rounded' },
  { id: 'notch', label: 'Notch',   hint: 'Everyday revere' },
];

export default function CustomFabricQuestionnaire({
  open, onClose, onSubmit,
  fabric,             // { id, sku, name, image_url, primary_color_hex, color_family, composition, weave, texture_notes }
  defaultCategory = 'full_suits',
  defaultSilhouetteId = 'classic_suit',
  generating = false,
  hasClientImage = true,
}) {
  const [categoryKey, setCategoryKey]   = useState(defaultCategory);
  const [silhouetteId, setSilhouetteId] = useState(defaultSilhouetteId);
  const [shoeStyle,    setShoeStyle]    = useState('oxford');
  const [shoeColour,   setShoeColour]   = useState('Black');
  const [shirtStyle,   setShirtStyle]   = useState('spread_collar');
  const [shirtColour,  setShirtColour]  = useState('White');
  // Feb 2026 — trouser cut for sport-coat renders. Business-casual
  // only. Iter 156 — refactored from a single-select pantCut chip
  // into independent multi-select state so the CFQ matches the
  // CustomizeModal Restructure · Trousers & Shoes panel exactly.
  // The composite `trouser_style` string is built on submit.
  const [pantHex,      setPantHex]      = useState('#36454F');
  const [pantName,     setPantName]     = useState('Charcoal');
  const [trPleats,     setTrPleats]     = useState(null);   // 'none' | 'single' | 'double' | null
  const [trCuffs,      setTrCuffs]      = useState(false);
  const [trWaist,      setTrWaist]      = useState(null);   // null | 'belt' | 'side_tabs'
  const [trBeltColor,  setTrBeltColor]  = useState(null);   // e.g. 'Black' | 'Cognac' | ...
  // Iter 113 — No-tie opt-in for library-fabric renders. Matches the
  // AddCustomSwatchModal toggle so both custom-fabric entry points offer
  // the same coverage.
  const [noTie,        setNoTie]        = useState(false);
  // Iter 127 — lapel + pocket selectors (jackets only).
  const [lapel,        setLapel]        = useState('notch');
  const [pocketStyle,  setPocketStyle]  = useState('');
  // Feb 2026 — vest customisation (three-piece + tuxedo only).
  // Defaults: SB with 5 buttons + no lapel — the classic three-piece
  // waistcoat. Tuxedo silhouettes still get a tuxedo-appropriate vest
  // (backend detects the tuxedo garment type and adapts fabric).
  const [vestStyle,    setVestStyle]    = useState('single_breasted');
  const [vestButtons,  setVestButtons]  = useState('5');
  const [vestLapel,    setVestLapel]    = useState('none');

  // Reset every time the dialog re-opens so a previous selection from a
  // different fabric doesn't bleed in.
  useEffect(() => {
    if (open) {
      setCategoryKey(defaultCategory);
      setSilhouetteId(defaultSilhouetteId);
      setShoeStyle('oxford');
      setShoeColour('Black');
      setShirtStyle('spread_collar');
      setShirtColour('White');
      setPantHex('#36454F');
      setPantName('Charcoal');
      setTrPleats(null);
      setTrCuffs(false);
      setTrWaist(null);
      setTrBeltColor(null);
      setNoTie(false);
      setLapel('notch');
      setPocketStyle('');
      setVestStyle('single_breasted');
      setVestButtons('5');
      setVestLapel('none');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fabric?.id]);

  const activeCategory = SILHOUETTE_TREE.find(c => c.key === categoryKey);

  // When category changes, reset silhouette to the first option in the
  // new category so the picker is always in a coherent state.
  const handleCategoryChange = (newKey) => {
    setCategoryKey(newKey);
    const cat = SILHOUETTE_TREE.find(c => c.key === newKey);
    setSilhouetteId(cat?.silhouettes[0]?.id || 'classic_suit');
  };

  const handleSubmit = () => {
    const cat = SILHOUETTE_TREE.find(c => c.key === categoryKey);
    const sil = cat?.silhouettes.find(s => s.id === silhouetteId);
    if (!sil) { toast.error('Pick a silhouette'); return; }
    const isShirtCategory = categoryKey === 'shirts';
    const isSportCoat     = categoryKey === 'business_casual';
    // Feb 2026 — vest fields only cross to the backend when the
    // silhouette actually supports a waistcoat (three-piece or tuxedo).
    // Everything else strips them so a stale state selection can't
    // sprout an unwanted vest onto a plain single-breasted suit.
    const _vestCapable = sil.id === 'three_piece' || sil.id === 'tuxedo';
    onSubmit?.({
      category: categoryKey,
      silhouetteId: sil.id,
      garmentName: sil.garment,
      // Shoe is irrelevant for shirts-only renders — send null so the
      // backend prompt skips the shoe clause altogether.
      shoe: isShirtCategory ? null : shoeStyle,
      shoeColour: isShirtCategory ? null : shoeColour,
      shirtStyle,
      shirtColour,
      // Iter 109 v5 — only send pant colour for sport-coat renders.
      // Full-suit + shirt paths use the fabric colour for trousers.
      pantHex:  isSportCoat ? pantHex  : null,
      pantName: isSportCoat ? pantName : null,
      // Iter 156 — compose the trouser_style composite from the
      // pleats + cuffs + waistband + belt-colour picks so the
      // render carries every restructure attribute the tailor
      // selected. Empty composite → backend keeps its default
      // (flat-front, belt loops + shoe-matched belt).
      //
      // Iter 234 (Feb 2026) — previously restricted to sport-coat
      // (`isSportCoat`) which meant a tailor building a SUIT could
      // never send side_tabs (or any pleats / cuffs). Now emits for
      // ANY non-shirt category — full suits render matching trousers
      // that also need the same structural options.
      pantCut: (() => {
        if (isShirtCategory) return null;
        return buildTrouserStyleTokens({ trPleats, trCuffs, trWaist, trBeltColor });
      })(),
      noTie,
      // Iter 127 — jacket construction (irrelevant for shirt renders).
      lapel: isShirtCategory ? null : lapel,
      pocketStyle: isShirtCategory ? null : (pocketStyle || null),
      // Feb 2026 — vest customisation.
      vestStyle:   _vestCapable ? vestStyle   : null,
      vestButtons: _vestCapable ? vestButtons : null,
      vestLapel:   _vestCapable ? vestLapel   : null,
    });
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !generating && onClose?.()}>
      <DialogContent
        className="max-w-3xl bg-[#0A0A0C] border border-[#27272A] rounded-none p-0 max-h-[92vh] overflow-y-auto"
        data-testid="custom-fabric-questionnaire-modal"
      >
        <DialogHeader className="px-6 py-5 border-b border-[#27272A] sticky top-0 bg-[#0A0A0C] z-20">
          {/* Explicit close button — sits above the sticky header so it
              stays reachable on every scroll position. The Radix Dialog
              built-in close gets buried behind the sticky header so we
              render our own. */}
          <button
            type="button"
            onClick={() => !generating && onClose?.()}
            disabled={generating}
            aria-label="Close"
            className="absolute right-3 top-3 z-30 w-8 h-8 flex items-center justify-center text-[#A1A1AA] hover:text-[#F5F5F5] hover:bg-[#1A1A1E] border border-[#27272A] hover:border-[#52525B] transition-colors disabled:opacity-40 disabled:pointer-events-none"
            data-testid="cfq-close-x"
          >
            <X className="w-4 h-4" />
          </button>
          <DialogTitle className="text-2xl font-light text-[#F5F5F5] flex items-center gap-3 pr-10" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {fabric?.image_url && (
              <img
                src={fabric.image_url}
                alt={fabric.name || fabric.sku}
                className="w-10 h-10 border border-[#27272A] object-cover"
              />
            )}
            Render in {fabric?.name || fabric?.sku || 'Custom Fabric'}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A1A1AA] mt-1">
            Pick the silhouette, shoe and shirt details — then we&apos;ll render this exact look in the chosen fabric.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6">
          {/* ── 1. Category + Silhouette ─────────────────────────────── */}
          <section>
            <Label className="label-luxury mb-2 block">Category</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {SILHOUETTE_TREE.map(cat => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => handleCategoryChange(cat.key)}
                  className={`px-3 py-2.5 text-xs uppercase tracking-wider border transition-all ${
                    categoryKey === cat.key
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                      : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                  }`}
                  data-testid={`cfq-cat-${cat.key}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <Label className="label-luxury mb-2 block">Silhouette</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(activeCategory?.silhouettes || []).map(sil => (
                <button
                  key={sil.id}
                  type="button"
                  onClick={() => setSilhouetteId(sil.id)}
                  className={`p-3 border text-left transition-all ${
                    silhouetteId === sil.id
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                      : 'border-[#27272A] hover:border-[#52525B]'
                  }`}
                  data-testid={`cfq-sil-${sil.id}`}
                >
                  <p className="text-sm text-[#F5F5F5]">{sil.label}</p>
                </button>
              ))}
            </div>
          </section>

          {/* ── 2 + 3. Shoe + Shirt (side-by-side on desktop) ────────── */}
          <section className="pt-2 border-t border-[#27272A] grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Shoe */}
            <div className={categoryKey === 'shirts' ? 'opacity-40 pointer-events-none' : ''}>
              <Label className="label-luxury mb-2 flex items-center gap-2">
                <Footprints className="w-3.5 h-3.5" /> Shoe
                {categoryKey === 'shirts' && (
                  <span className="text-[10px] text-[#52525B] uppercase normal-case ml-auto">N/A for shirts</span>
                )}
              </Label>
              <div className="space-y-3">
                <div>
                  <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Style</span>
                  <div className="grid grid-cols-2 gap-1.5">
                    {SHOE_STYLES.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setShoeStyle(s.id)}
                        className={`px-2 py-2 text-xs border transition-all ${
                          shoeStyle === s.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                            : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                        }`}
                        data-testid={`cfq-shoe-${s.id}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Colour</span>
                  <div className="grid grid-cols-4 gap-1.5">
                    {SHOE_COLOURS.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setShoeColour(c.id)}
                        className={`flex flex-col items-center gap-1 p-2 border transition-all ${
                          shoeColour === c.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                            : 'border-[#27272A] hover:border-[#52525B]'
                        }`}
                        data-testid={`cfq-shoe-color-${c.id.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="w-6 h-6 border border-black/30" style={{ backgroundColor: c.hex }} />
                        <span className="text-[10px] text-[#A1A1AA] leading-tight text-center">{c.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Shirt */}
            <div>
              <Label className="label-luxury mb-2 flex items-center gap-2">
                <ShirtIcon className="w-3.5 h-3.5" /> Shirt
              </Label>
              <div className="space-y-3">
                <div>
                  <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Style</span>
                  <div className="grid grid-cols-2 gap-1.5">
                    {SHIRT_STYLES.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setShirtStyle(s.id)}
                        className={`px-2 py-2 text-xs border transition-all ${
                          shirtStyle === s.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                            : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                        }`}
                        data-testid={`cfq-shirt-${s.id}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Colour</span>
                  <div className="grid grid-cols-4 gap-1.5">
                    {SHIRT_COLOURS.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setShirtColour(c.id)}
                        className={`flex flex-col items-center gap-1 p-2 border transition-all ${
                          shirtColour === c.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                            : 'border-[#27272A] hover:border-[#52525B]'
                        }`}
                        data-testid={`cfq-shirt-color-${c.id.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="w-6 h-6 border border-black/30" style={{ backgroundColor: c.hex }} />
                        <span className="text-[10px] text-[#A1A1AA] leading-tight text-center">{c.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Iter 127 — Lapel + Pockets (jackets only) ────────────── */}
          {categoryKey !== 'shirts' && (
            <section className="pt-4 border-t border-[#27272A] grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="cfq-jacket-details">
              <div>
                <Label className="label-luxury mb-2 block">Lapel</Label>
                <div className="grid grid-cols-3 gap-1.5">
                  {LAPEL_STYLES.map(l => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setLapel(l.id)}
                      className={`px-2 py-2 text-xs border transition-all ${
                        lapel === l.id
                          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                          : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                      }`}
                      data-testid={`cfq-lapel-${l.id}`}
                    >
                      <span className="block">{l.label}</span>
                      <span className="block text-[9px] text-[#52525B] mt-0.5">{l.hint}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="label-luxury mb-2 block">Hip Pockets</Label>
                <div className="grid grid-cols-4 gap-1.5">
                  {POCKET_CHOICES.map(p => (
                    <button
                      key={p.id || 'none'}
                      type="button"
                      onClick={() => setPocketStyle(p.id)}
                      className={`px-2 py-2 text-xs border transition-all ${
                        pocketStyle === p.id
                          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                          : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                      }`}
                      data-testid={`cfq-pocket-${p.id || 'none'}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ── Feb 2026 — Vest / Waistcoat (three-piece + tuxedo only) ──
              Surfaced only when the selected silhouette actually has a
              third piece. SB → 4/5/6 buttons + no-lapel/notch/shawl.
              DB → 4×2/6×2 buttons + peak/shawl/notch lapel. */}
          {(silhouetteId === 'three_piece' || silhouetteId === 'tuxedo') && (
            <section
              className="pt-4 border-t border-[#27272A] space-y-4"
              data-testid="cfq-vest-section"
            >
              <div>
                <Label className="label-luxury mb-2 block">
                  Vest / Waistcoat
                  <span className="text-[10px] text-[#52525B] uppercase tracking-wider normal-case ml-2">
                    Third-piece construction
                  </span>
                </Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {VEST_STYLES.map(v => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => {
                        setVestStyle(v.id);
                        // Reset button + lapel defaults to a coherent
                        // pair when switching between SB and DB, since
                        // '5' buttons is not a valid DB configuration
                        // and 'peak' is a DB-only default.
                        if (v.id === 'double_breasted') {
                          setVestButtons('6x2');
                          setVestLapel('peak');
                        } else {
                          setVestButtons('5');
                          setVestLapel('none');
                        }
                      }}
                      className={`p-3 border text-left transition-all ${
                        vestStyle === v.id
                          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                          : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                      }`}
                      data-testid={`cfq-vest-style-${v.id}`}
                    >
                      <p className="text-xs uppercase tracking-wider">{v.label}</p>
                      <p className="text-[9px] text-[#52525B] mt-0.5 normal-case">{v.hint}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Label className="label-luxury mb-2 block">Buttons</Label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {(vestStyle === 'double_breasted' ? VEST_BUTTONS_DB : VEST_BUTTONS_SB).map(b => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setVestButtons(b.id)}
                        className={`px-2 py-2 text-xs border transition-all ${
                          vestButtons === b.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                            : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                        }`}
                        data-testid={`cfq-vest-buttons-${b.id}`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="label-luxury mb-2 block">Vest Lapel</Label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {(vestStyle === 'double_breasted' ? VEST_LAPELS_DB : VEST_LAPELS_SB).map(l => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => setVestLapel(l.id)}
                        className={`px-2 py-2 text-xs border transition-all ${
                          vestLapel === l.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]'
                            : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                        }`}
                        data-testid={`cfq-vest-lapel-${l.id}`}
                      >
                        <span className="block">{l.label}</span>
                        <span className="block text-[9px] text-[#52525B] mt-0.5">{l.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}
          {/* ── Trouser picker · Sport-Coat only ─────────────────────────
              Sport coats pair with contrast trousers rather than a
              matching fabric bottom, so the tailor picks the pant
              colour here. Hidden for suits (jacket + pant match the
              fabric) and shirts (no pants).
              Iter 234 (Feb 2026) — TROUSER RESTRUCTURE (pleats / cuffs
              / waistband / belt) MOVED OUT of the sport-coat-only gate
              so tailors can pick side tabs on SUITS too. Only the pant
              COLOUR picker remains business-casual-only; suit pants
              use the jacket fabric so colour is inherited, but the
              structural options (side tabs vs belt loops, pleats,
              cuffs) still matter and must be reachable. */}
          {categoryKey === 'business_casual' && (
            <section
              className="pt-4 border-t border-[#27272A]"
              data-testid="cfq-pant-section"
            >
              <Label className="label-luxury mb-2 flex items-center gap-2">
                <span>Trousers</span>
                <span className="text-[10px] text-[#52525B] uppercase tracking-wider normal-case ml-2">
                  {pantName} — sport coat pairs with this trouser colour
                </span>
              </Label>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                {PANT_COLORS.map(c => {
                  const selected = pantHex === c.value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => { setPantHex(c.value); setPantName(c.name); }}
                      title={c.name}
                      className={`flex flex-col items-center gap-1 p-2 border transition-all ${
                        selected
                          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                          : 'border-[#27272A] hover:border-[#52525B]'
                      }`}
                      data-testid={`cfq-pant-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="w-6 h-6 border border-black/30" style={{ backgroundColor: c.value }} />
                      <span className="text-[10px] text-[#A1A1AA] leading-tight text-center">{c.name}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* ── Trouser Restructure — pleats / cuffs / waistband / belt ──
              Iter 234 (Feb 2026) — shown for ANY category that renders
              trousers (suits, sport coats — everything except plain
              shirts). Suit tailors kept telling us they couldn't pick
              side tabs on their jackets because the whole block was
              hidden behind a business-casual gate. Split out here. */}
          {categoryKey !== 'shirts' && (
            <section
              className="pt-4 border-t border-[#27272A]"
              data-testid="cfq-trouser-restructure-section"
            >
              <Label className="label-luxury mb-1 flex items-center gap-2">
                <span>Trouser Restructure</span>
                <span className="text-[10px] text-[#52525B] uppercase tracking-wider normal-case ml-2">
                  {categoryKey === 'business_casual'
                    ? 'Pleats · cuffs · waistband — for the contrast trouser above'
                    : 'Pleats · cuffs · waistband — for the matching suit trouser'}
                </span>
              </Label>
              <TrouserRestructureBlock
                trPleats={trPleats}       setTrPleats={setTrPleats}
                trCuffs={trCuffs}         setTrCuffs={setTrCuffs}
                trWaist={trWaist}         setTrWaist={setTrWaist}
                trBeltColor={trBeltColor} setTrBeltColor={setTrBeltColor}
                testIdPrefix="cfq-trouser"
              />
            </section>
          )}

          {/* Iter 113 — No-tie opt-in toggle. Hidden for `shirts` (no
              tie in frame). Sends `tie_type='none'` when checked. */}
          {categoryKey !== 'shirts' && (
            <section
              className="pt-4 border-t border-[#27272A]"
              data-testid="cfq-notie-section"
            >
              <button
                type="button"
                onClick={() => setNoTie(v => !v)}
                className={`w-full flex items-center gap-3 p-3 border transition-all text-left ${
                  noTie
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                    : 'border-[#27272A] hover:border-[#52525B]'
                }`}
                data-testid="cfq-notie-toggle"
                aria-pressed={noTie}
              >
                <div className={`w-4 h-4 border ${noTie ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))]' : 'border-[#52525B]'} flex items-center justify-center flex-shrink-0`}>
                  {noTie && (
                    <svg className="w-3 h-3 text-[#0A0A0C]" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 6.5L5 9L10 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-widest text-[#F5F5F5] font-medium">No Tie</p>
                  <p className="text-[10px] text-[#52525B] uppercase tracking-wider normal-case mt-0.5">
                    Render with an open collar — no necktie, no bow tie, no ascot
                  </p>
                </div>
              </button>
            </section>
          )}

          {/* Submit */}
          <div className="pt-4 border-t border-[#27272A] flex flex-col gap-3">
            {!hasClientImage && (
              <div
                className="px-4 py-3 border border-[#4F2F1F] bg-[#2A1A0F] flex items-start gap-3"
                data-testid="cfq-no-client-photo-warning"
              >
                <div className="text-[#D4A87A] text-sm mt-0.5">⚠</div>
                <div className="flex-1">
                  <p className="text-[11px] uppercase tracking-widest text-[#D4A87A] font-semibold">
                    Client photo required
                  </p>
                  <p className="text-[11px] text-[#A1A1AA] mt-1 leading-relaxed">
                    Custom Fabric renders need the client&apos;s face reference. Close this modal and upload a photo in the <em>Client Photo</em> panel, then click Render again.
                  </p>
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={generating}
              className="text-[#A1A1AA] hover:bg-[#1A1A1E] rounded-none"
              data-testid="cfq-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={generating || !hasClientImage}
              title={!hasClientImage ? 'Upload a client photo first — the render needs a face reference' : undefined}
              className="rounded-none uppercase tracking-wider text-xs px-6 py-5 disabled:opacity-40"
              style={{ backgroundColor: 'hsl(var(--accent))', color: '#0A0A0C' }}
              data-testid="cfq-submit"
            >
              {generating
                ? (<><Loader2 className="w-4 h-4 spinner mr-2" /> Rendering…</>)
                : (<><Sparkles className="w-4 h-4 mr-2" /> Render Look</>)}
            </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
